import { useState } from 'react';
import {
  Plus, Clock, Shield, LogOut, Activity, Search, Settings, ChevronsLeft, ChevronsRight, Sparkles, Users, ClipboardCheck, Building2, UserPlus
} from 'lucide-react';
import { cn, getInitials, formatNumber } from '../lib/utils';
import { LogoMark, LogoLockup } from './ui/Logo';

const navItems = [
  { id: 'new',       label: 'Nowe zlecenie',  icon: Plus,    badge: null },
  { id: 'live',      label: 'Live Dashboard', icon: Activity, badge: 'NEW' },
  { id: 'history',   label: 'Historia',       icon: Clock,   badge: null },
  { id: 'settings',  label: 'Ustawienia',     icon: Settings, badge: null },
];

const adminItems = [
  { id: 'clients',   label: 'Klienci',        icon: Users,   badge: 'v6' },
  { id: 'readiness', label: 'Gotowość',       icon: ClipboardCheck, badge: 'v6' },
  { id: 'team',      label: 'Zespół',         icon: UserPlus, badge: null },
  { id: 'admin',     label: 'Panel Admina',   icon: Shield,  badge: null },
];

// Plan v8/E6: pracownik / właściciel organizacji (agencja) — klienci organizacji, Gotowość, zespół.
const orgItems = [
  { id: 'clients',   label: 'Klienci',        icon: Users,   badge: null },
  { id: 'readiness', label: 'Gotowość',       icon: ClipboardCheck, badge: null },
  { id: 'team',      label: 'Zespół',         icon: UserPlus, badge: null },
];

const clientItems = [
  { id: 'myclient',  label: 'Mój klient',     icon: Building2, badge: null },
  { id: 'readiness', label: 'Gotowość',       icon: ClipboardCheck, badge: null },
];

export default function Sidebar({ page, setPage, user, credits, creditsInfo = null, isAdmin, clientTenantId, org = null, onLogout, onOpenPalette }) {
  const [collapsed, setCollapsed] = useState(false);
  const email = user?.email || '';
  const initials = getInitials(email);
  const creditsValue = credits ?? 0;
  const unlimited = creditsInfo?.unlimited || creditsValue === Infinity;
  const creditsLow = !unlimited && creditsValue < (creditsInfo?.scope === 'org' ? 10 : 1000);
  const creditsLabel = unlimited ? 'bez limitu' : formatNumber(creditsValue);
  // T2.3: konto klienta agencji = podgląd — bez „Nowe zlecenie”, Live Dashboard i kredytów.
  const clientView = !isAdmin && !!clientTenantId && !org;
  const mainItems = clientView ? navItems.filter(i => i.id === 'history' || i.id === 'settings') : navItems;

  const renderItem = (item) => {
    const Icon = item.icon;
    const active = page === item.id;
    return (
      <button
        key={item.id}
        type="button"
        onClick={() => setPage(item.id)}
        title={collapsed ? item.label : undefined}
        className={cn(
          'group relative w-full flex items-center gap-3 rounded-md text-sm font-medium tracking-tightish transition-all duration-150',
          collapsed ? 'h-10 justify-center' : 'h-9 px-3',
          active
            ? 'bg-brand-50 text-brand-700 dark:bg-brand-900/40 dark:text-brand-200'
            : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900 dark:text-ink-400 dark:hover:bg-ink-800 dark:hover:text-ink-100'
        )}
      >
        <Icon className={cn('w-[18px] h-[18px] flex-shrink-0', active && 'text-brand-600 dark:text-brand-300')} strokeWidth={active ? 2.4 : 2} />
        {!collapsed && (
          <>
            <span className="flex-1 text-left">{item.label}</span>
            {item.badge && (
              <span className="px-1.5 py-0.5 rounded text-2xs font-bold uppercase bg-gradient-to-r from-brand-500 to-brand-700 text-white tracking-wide">
                {item.badge}
              </span>
            )}
          </>
        )}
        {active && !collapsed && (
          <span className="absolute left-0 top-1.5 bottom-1.5 w-0.5 rounded-r-full bg-brand-600 dark:bg-brand-400" />
        )}
        {/* Collapsed: dot indicator zamiast lewego rail (rail jest chowany za krawędź) */}
        {active && collapsed && (
          <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-brand-600 dark:bg-brand-400 ring-2 ring-white dark:ring-ink-900" />
        )}
      </button>
    );
  };

  return (
    <aside
      className={cn(
        'fixed left-0 top-0 bottom-0 z-40 flex flex-col bg-white border-r border-ink-200 dark:bg-ink-900 dark:border-ink-800',
        'transition-[width] duration-200 ease-out',
        collapsed ? 'w-[72px]' : 'w-[248px]',
      )}
    >
      {/* Logo + collapse toggle */}
      <div className={cn(
        'flex items-center border-b border-ink-100 dark:border-ink-800',
        collapsed ? 'h-16 justify-center' : 'h-16 px-4 justify-between'
      )}>
        {collapsed ? <LogoMark size="md" /> : <LogoLockup size="md" />}
        {!collapsed && (
          <button
            type="button"
            onClick={() => setCollapsed(true)}
            className="text-ink-400 hover:text-ink-700 hover:bg-ink-100 dark:hover:text-ink-200 dark:hover:bg-ink-800 p-1.5 rounded transition-colors"
            title="Zwiń sidebar"
          >
            <ChevronsLeft className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Cmd+K command palette trigger */}
      {!collapsed && (
        <button
          type="button"
          onClick={onOpenPalette}
          className="mx-3 mt-3 mb-1 flex items-center gap-2 h-9 px-3 rounded-md border border-ink-200 bg-ink-50/50 hover:bg-white hover:border-brand-300 text-sm text-ink-500 hover:text-ink-700 transition-colors dark:border-ink-700 dark:bg-ink-800/50 dark:hover:bg-ink-800 dark:hover:border-brand-700 dark:text-ink-400 dark:hover:text-ink-200"
        >
          <Search className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 text-left">Szukaj…</span>
          <kbd className="px-1.5 py-0.5 rounded text-2xs font-mono bg-white border border-ink-200 text-ink-500 dark:bg-ink-900 dark:border-ink-700 dark:text-ink-400">⌘K</kbd>
        </button>
      )}

      {/* Nav */}
      <nav className={cn('flex-1 overflow-y-auto', collapsed ? 'px-2 py-3' : 'px-3 py-3')}>
        {!collapsed && (
          <div className="px-2 mb-1.5 text-2xs font-bold uppercase tracking-[0.08em] text-ink-400 dark:text-ink-500">
            Workspace
          </div>
        )}
        <div className="space-y-0.5">
          {mainItems.map(renderItem)}
        </div>

        {!isAdmin && clientTenantId && (
          <>
            {!collapsed && (
              <div className="px-2 mt-6 mb-1.5 text-2xs font-bold uppercase tracking-[0.08em] text-ink-400 dark:text-ink-500">
                Twój katalog
              </div>
            )}
            <div className="space-y-0.5">
              {clientItems.map(renderItem)}
            </div>
          </>
        )}

        {!isAdmin && org && (
          <>
            {!collapsed && (
              <div className="px-2 mt-6 mb-1.5 text-2xs font-bold uppercase tracking-[0.08em] text-ink-400 dark:text-ink-500 truncate" title={org.name}>
                {org.name}
              </div>
            )}
            <div className="space-y-0.5">
              {orgItems.map(renderItem)}
            </div>
          </>
        )}

        {isAdmin && (
          <>
            {!collapsed && (
              <div className="px-2 mt-6 mb-1.5 text-2xs font-bold uppercase tracking-[0.08em] text-ink-400 dark:text-ink-500">
                Administracja
              </div>
            )}
            <div className="space-y-0.5">
              {adminItems.map(renderItem)}
            </div>
          </>
        )}
      </nav>

      {/* Credits widget */}
      {clientView ? null : !collapsed ? (
        <div className="mx-3 mb-3 rounded-lg overflow-hidden border border-ink-200 dark:border-ink-700 shadow-xs">
          <div className="bg-gradient-to-br from-brand-600 to-brand-800 px-3.5 py-2.5 flex items-center justify-between">
            <span className="text-[10px] font-bold uppercase tracking-[0.08em] text-white/75 truncate" title={creditsInfo?.scope === 'org' ? `Pula organizacji: ${creditsInfo.orgName ?? ''}` : undefined}>
              {creditsInfo?.scope === 'org' ? `Kredyty · ${creditsInfo.orgName ?? 'organizacja'}` : 'Kredyty'}
            </span>
            <Sparkles className="w-3.5 h-3.5 text-white/60" />
          </div>
          <div className="bg-white dark:bg-ink-800 px-3.5 py-3">
            <div className="flex items-baseline gap-1">
              <span className={cn(
                'text-2xl font-extrabold tracking-tighter leading-none',
                creditsLow
                  ? 'text-warning-dark dark:text-warning'
                  : 'bg-gradient-to-br from-brand-700 to-brand-950 bg-clip-text text-transparent dark:from-brand-200 dark:to-brand-400',
              )}>
                {unlimited ? '∞' : formatNumber(creditsValue)}
              </span>
              <span className="text-xs text-ink-400 dark:text-ink-500 font-medium">{unlimited ? 'bez limitu' : 'dost.'}</span>
            </div>
            <button
              type="button"
              onClick={() => setPage('settings')}
              className="mt-2 w-full h-7 text-xs font-semibold rounded bg-brand-50 text-brand-700 hover:bg-brand-100 dark:bg-brand-900/40 dark:text-brand-300 dark:hover:bg-brand-900/60 transition-colors"
            >
              + Doładuj
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          title={`Kredyty: ${creditsLabel}`}
          onClick={() => setPage('settings')}
          className="mx-2 mb-3 h-10 rounded-md bg-gradient-to-br from-brand-600 to-brand-800 text-white text-2xs font-bold flex items-center justify-center"
        >
          {unlimited ? '∞' : creditsValue > 9999 ? `${Math.floor(creditsValue / 1000)}k` : formatNumber(creditsValue)}
        </button>
      )}

      {/* User block */}
      <div className={cn(
        'border-t border-ink-100 dark:border-ink-800 p-3 flex items-center gap-2.5',
        collapsed && 'justify-center'
      )}>
        <div className="relative flex-shrink-0">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-brand-500 to-brand-700 text-white flex items-center justify-center text-xs font-bold shadow-sm">
            {initials}
          </div>
          <span className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-success ring-2 ring-white dark:ring-ink-900" />
        </div>
        {!collapsed && (
          <>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-semibold text-ink-800 dark:text-ink-100 truncate">{email}</div>
              <div className="text-2xs text-ink-500 dark:text-ink-400 mt-0.5">Online</div>
            </div>
            <button
              type="button"
              onClick={onLogout}
              title="Wyloguj"
              className="p-1.5 rounded text-ink-400 hover:text-danger hover:bg-danger-light/50 dark:hover:bg-danger/20 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </>
        )}
      </div>

      {/* Expand handle when collapsed */}
      {collapsed && (
        <button
          type="button"
          onClick={() => setCollapsed(false)}
          className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-white border border-ink-200 shadow-sm flex items-center justify-center text-ink-500 hover:text-brand-600 hover:border-brand-300 dark:bg-ink-800 dark:border-ink-700 dark:text-ink-400 dark:hover:text-brand-400 transition-colors"
          title="Rozwiń sidebar"
        >
          <ChevronsRight className="w-3.5 h-3.5" />
        </button>
      )}
    </aside>
  );
}
